import React from 'react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  AreaChart, Area, XAxis, YAxis, CartesianGrid
} from 'recharts';
import { MapPin, Target, FileText, Users, TrendingUp, Navigation } from 'lucide-react';
import type { ProjectOverviewReport as ProjectOverviewReportType } from '../../services/reporting';

interface ProjectOverviewReportProps {
  projectOverview: ProjectOverviewReportType | null;
  loading: boolean;
}

const ProjectOverviewReport: React.FC<ProjectOverviewReportProps> = ({
  projectOverview,
  loading
}) => {
  if (!projectOverview && !loading) {
    return (
      <div className="text-center text-gray-500 py-12">
        <p>Select a project and click Refresh Data to view the overview report.</p>
      </div>
    );
  }

  if (!projectOverview) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <MapPin className="h-5 w-5 text-indigo-500 mr-2" />
            <p className="text-sm text-gray-500">Total Addresses</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{projectOverview.summary.totalAddresses.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Target className="h-5 w-5 text-green-500 mr-2" />
            <p className="text-sm text-gray-500">Completed</p>
          </div>
          <p className="text-2xl font-bold text-green-600">{projectOverview.summary.addressesCompleted.toLocaleString()}</p>
          <p className="text-sm text-green-600">{projectOverview.summary.overallCompletionRate}% completion</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FileText className="h-5 w-5 text-blue-500 mr-2" />
            <p className="text-sm text-gray-500">Surveys</p>
          </div>
          <p className="text-2xl font-bold text-blue-600">{projectOverview.summary.totalSurveyResponses.toLocaleString()}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-5 w-5 text-purple-500 mr-2" />
            <p className="text-sm text-gray-500">Canvassers</p>
          </div>
          <p className="text-2xl font-bold text-purple-600">{projectOverview.summary.totalCanvassers}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="h-5 w-5 text-orange-500 mr-2" />
            <p className="text-sm text-gray-500">Field Hours</p>
          </div>
          <p className="text-2xl font-bold text-orange-600">{projectOverview.summary.totalFieldHours}</p>
        </div>
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Status Breakdown</h3>
          {Object.values(projectOverview.statusBreakdown).some(count => count > 0) ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={Object.entries(projectOverview.statusBreakdown)
                      .filter(([_, count]) => count > 0)
                      .map(([status, count]) => ({
                        name: status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1),
                        value: count,
                      }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={70}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => percent > 0.03 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}
                    labelLine={({ percent }) => percent > 0.03}
                  >
                    {Object.entries(projectOverview.statusBreakdown)
                      .filter(([_, count]) => count > 0)
                      .map((_, index) => (
                        <Cell key={`cell-${index}`} fill={['#9CA3AF', '#60A5FA', '#FCD34D', '#F87171', '#34D399', '#F97316', '#6B7280'][index % 7]} />
                      ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => value.toLocaleString()} />
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value, entry: any) => {
                      const total = Object.values(projectOverview.statusBreakdown).reduce((sum, v) => sum + v, 0);
                      const percent = total > 0 ? ((entry.payload.value / total) * 100).toFixed(1) : '0';
                      return `${value} (${percent}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-500">
              No status data available.
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-4">Daily Activity Trend</h3>
          {projectOverview.dailyTrends && projectOverview.dailyTrends.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={projectOverview.dailyTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(val) => val?.slice?.(5) || val} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="addressesVisited" name="Visited" stroke="#6366F1" fill="#6366F1" fillOpacity={0.3} />
                  <Area type="monotone" dataKey="addressesCompleted" name="Completed" stroke="#22C55E" fill="#22C55E" fillOpacity={0.3} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No daily trend data available for this time range.
            </div>
          )}
        </div>
      </div>

      {/* Top Canvassers */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Top Canvassers</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Visits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completion Rate</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Surveys</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time/Addr</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projectOverview.topCanvassers.map((canvasser, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{canvasser.userName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.totalVisits}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.completedAddresses}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.completionRate}%</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.surveysCompleted}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{canvasser.avgTimePerAddress} min</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Useful Metrics */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-4">Key Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Workload Progress */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <Navigation className="h-4 w-4 mr-2 text-indigo-500" />
              Workload Progress
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Total Workloads</span>
                <span className="text-sm font-medium">{projectOverview.usefulMetrics.workloadProgress.total}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Active</span>
                <span className="text-sm font-medium text-green-600">{projectOverview.usefulMetrics.workloadProgress.active}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Completed</span>
                <span className="text-sm font-medium text-blue-600">{projectOverview.usefulMetrics.workloadProgress.completed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Draft</span>
                <span className="text-sm font-medium text-gray-500">{projectOverview.usefulMetrics.workloadProgress.draft}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-sm text-gray-500">Avg Completion</span>
                <span className="text-sm font-bold text-indigo-600">{projectOverview.usefulMetrics.workloadProgress.avgCompletionRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Avg Addresses/Workload</span>
                <span className="text-sm font-medium">{projectOverview.usefulMetrics.workloadProgress.avgAddressesPerWorkload}</span>
              </div>
            </div>
          </div>

          {/* Contact Efficiency */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <Users className="h-4 w-4 mr-2 text-green-500" />
              Contact Efficiency
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Total Attempts</span>
                <span className="text-sm font-medium">{projectOverview.usefulMetrics.contactEfficiency.totalAttempts}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Contacts Made</span>
                <span className="text-sm font-medium text-green-600">{projectOverview.usefulMetrics.contactEfficiency.contactsMade}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-sm text-gray-500">Contact Rate</span>
                <span className="text-sm font-bold text-green-600">{projectOverview.usefulMetrics.contactEfficiency.contactRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Not Home</span>
                <span className="text-sm font-medium text-yellow-600">
                  {projectOverview.usefulMetrics.contactEfficiency.notHomeCount} ({projectOverview.usefulMetrics.contactEfficiency.notHomeRate}%)
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Refused</span>
                <span className="text-sm font-medium text-red-600">
                  {projectOverview.usefulMetrics.contactEfficiency.refusedCount} ({projectOverview.usefulMetrics.contactEfficiency.refusedRate}%)
                </span>
              </div>
            </div>
          </div>

          {/* Survey Metrics */}
          <div className="border rounded-lg p-4">
            <h4 className="font-medium text-gray-700 mb-3 flex items-center">
              <FileText className="h-4 w-4 mr-2 text-blue-500" />
              Survey Metrics
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Total Responses</span>
                <span className="text-sm font-medium text-blue-600">{projectOverview.usefulMetrics.surveyMetrics.totalSurveyResponses}</span>
              </div>
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="text-sm text-gray-500">Survey Completion Rate</span>
                <span className="text-sm font-bold text-blue-600">{projectOverview.usefulMetrics.surveyMetrics.surveyCompletionRate}%</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Completion rate = surveys completed / contacts made
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverviewReport;
